import React from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { SELECTED_BG, SELECTED_FG, SELECTED_BG_HOVER } from '../config/uiTheme';

// What the note markers spell out. Same three values the toolbar button used to
// cycle through, laid flat so the current one is readable without tapping.
const LABEL_OPTIONS = [
  { value: 'none', label: 'Off' },
  { value: 'degrees', label: 'Degrees' },
  { value: 'names', label: 'Names' },
];

// Chord degree the green anchor follows across the progression. Voice leading is
// about a function, not a fixed pitch, so the anchor is recomputed on every chord.
// Stops at the 7th on purpose: 1/3/5/7 sound right on all seven diatonic degrees,
// while 9/11/13 flip between colour and avoid note depending on the chord (the
// 11th is the note to dodge on a Imaj7 and the best colour on a IVmaj7), so a
// single "track the 11th" instruction would teach the wrong thing half the time.
const TARGET_OPTIONS = [
  { value: 1, label: '1', caption: 'Root' },
  { value: 3, label: '3', caption: 'Third' },
  { value: 5, label: '5', caption: 'Fifth' },
  { value: 7, label: '7', caption: 'Seventh' },
];

const SECTION_LABEL_SX = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'rgba(255, 255, 255, 0.45)',
  mb: 1,
};

const TOGGLE_GROUP_SX = {
  '& .MuiToggleButton-root': {
    color: 'rgba(255, 255, 255, 0.6)',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    textTransform: 'none',
    py: 0.75,
    '&.Mui-selected': {
      color: SELECTED_FG,
      backgroundColor: SELECTED_BG,
      '&:hover': { backgroundColor: SELECTED_BG_HOVER },
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.2)',
      borderColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
};

// Body of the popover anchored on the neck's View button. Groups what the neck
// shows: the note labels and the degree the green anchor tracks.
const NeckViewPanel = ({
  noteDisplayMode,
  onNoteDisplayModeChange,
  trackedDegree,
  onTrackedDegreeChange,
  chordExtension = '3',
}) => (
  <Box sx={{ p: 2, width: 300, bgcolor: '#15151A' }}>
    <Typography sx={SECTION_LABEL_SX}>Labels</Typography>
    <ToggleButtonGroup
      exclusive
      fullWidth
      size="small"
      value={noteDisplayMode}
      onChange={(e, next) => { if (next) onNoteDisplayModeChange(next); }}
      sx={{
        ...TOGGLE_GROUP_SX,
        '& .MuiToggleButton-root': {
          ...TOGGLE_GROUP_SX['& .MuiToggleButton-root'],
          fontWeight: 700,
          fontSize: '0.8rem',
        },
      }}
    >
      {LABEL_OPTIONS.map((o) => (
        <ToggleButton key={o.value} value={o.value} aria-label={o.label}>
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>

    <Typography sx={{ ...SECTION_LABEL_SX, mt: 2 }}>Target</Typography>
    <ToggleButtonGroup
      exclusive
      fullWidth
      value={trackedDegree}
      onChange={(e, next) => { if (next) onTrackedDegreeChange(next); }}
      sx={TOGGLE_GROUP_SX}
    >
      {TARGET_OPTIONS.map((o) => (
        <ToggleButton
          key={o.value}
          value={o.value}
          aria-label={o.caption}
          // The 7th only sounds in a 7th-chord voicing. Greyed out rather than
          // hidden, so the reason it is unavailable stays visible.
          disabled={o.value === 7 && chordExtension !== '7'}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
            <Box component="span" sx={{ fontSize: '1.4rem', fontWeight: 800 }}>{o.label}</Box>
            <Box component="span" sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>
              {o.caption}
            </Box>
          </Box>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Box>
);

export default NeckViewPanel;
